/**
 * TDO-1 — UserGuide subsection visibility predicate (extracted from
 * `UserGuide.tsx` so it is unit-testable without importing the page).
 *
 * Preserves the prior `featureGate` semantics exactly, then ANDs an optional
 * role gate. Conservative: when a role gate is set but the viewer's role is
 * unknown, the item is hidden. `status`/`tierLabel` are label-only — they never
 * hide content.
 */

export type GuideRole =
  | 'OWNER'
  | 'MANAGER'
  | 'ORG_ADMIN'
  | 'SERVICE_WRITER'
  | 'TECHNICIAN'
  | 'AUDITOR';

export interface GuideVisibilityItem {
  /** Feature key(s) required to view. If array, ANY match grants access. */
  featureGate?: string | string[];
  /** Optional role gate. Omitted ⇒ no role restriction. */
  requiredRoles?: GuideRole[];
}

export function canViewGuideItem(
  item: GuideVisibilityItem,
  role: GuideRole | undefined,
  featureFlags: Record<string, boolean> | null | undefined,
): boolean {
  // Feature gate — unchanged behavior.
  if (item.featureGate) {
    if (!featureFlags) return false;
    const gates = Array.isArray(item.featureGate) ? item.featureGate : [item.featureGate];
    if (!gates.some((f) => featureFlags[f])) return false;
  }
  // Role gate — omitted ⇒ no restriction; conservative when role is unknown.
  if (item.requiredRoles && item.requiredRoles.length > 0) {
    if (!role) return false;
    if (!item.requiredRoles.includes(role)) return false;
  }
  return true;
}
